import { ReactNode, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useNoIndex } from "@/hooks/use-no-index";


/**
 * Admin-only route guard. Replaces the deleted client-portal ProtectedRoute.
 * Auth is still used solely so the team can sign into /admin.
 */
const AdminProtectedRoute = ({ children }: { children: ReactNode }) => {
  // Admin surface is never indexed — noindex,nofollow (private data behind auth).
  useNoIndex(true, { nofollow: true });
  const [state, setState] = useState<"checking" | "allowed" | "denied">("checking");


  useEffect(() => {
    let active = true;

    // Reactive guard: re-evaluated on every auth change (sign-out, token refresh,
    // user switch) so a lost session or lost admin role denies access immediately
    // instead of leaving a previously authorized screen mounted.
    const evaluate = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!active) return;
      if (!session) { setState("denied"); return; }
      const { data, error } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", session.user.id)
        .eq("role", "admin")
        .maybeSingle();
      if (!active) return;
      setState(!error && data ? "allowed" : "denied");
    };

    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!active) return;
      if (!session) { setState("denied"); return; }
      void evaluate();
    });

    void evaluate();
    return () => { active = false; sub.subscription.unsubscribe(); };
  }, []);


  if (state === "checking") return null;
  if (state === "denied") return <Navigate to="/admin/login" replace />;
  return <>{children}</>;
};

export default AdminProtectedRoute;
